export const anatomy2Module = {
  id: "anatomy2",
  title: "Adrenal Gland Anatomy: Part 2",
  sections: [
    {
      type: "text",
      content: `
        <h3>Relations of the Adrenal Glands</h3>
        <p>The adrenal glands sit in the retroperitoneum at the superior pole of each kidney, enclosed within Gerota's 
        fascia together with the kidney. However, despite sharing the same fascial compartment, the adrenal glands have
         their <b>own separate blood supply</b> — so removing a kidney does not necessarily devascularise the adrenal
          gland on the same side.</p>
        <p>Knowing the relations of the adrenal glands is important in surgical planning (e.g. adrenalectomy) and for 
        understanding how tumours of the adrenal gland can compress or invade adjacent structures.</p>

        <h3>Right adrenal gland</h3>
        <p>The right adrenal gland is roughly <b>pyramidal (triangular)</b> in shape and sits directly posterior to the
         <b>inferior vena cava</b> (IVC), which makes right adrenalectomy more technically challenging than the left side 
         — the surgeon must work around the IVC.</p>
        <ul>
          <li><b>Anteriorly:</b> Bare area of the <b class="purple-term">liver</b> (not covered by peritoneum) and
           the <b class="purple-term">inferior vena cava</b> (IVC) medially</li>
          <li><b>Posteriorly:</b> <b class="purple-term">Diaphragm</b></li>
          <li><b>Inferiorly:</b> Superior pole of the <b>right kidney</b></li>
          <li><b>Medially:</b> <b class="purple-term">Inferior vena cava</b></li>
        </ul>
        <h3>Left adrenal gland</h3>
        <p>The left adrenal gland is more <b>semilunar (crescent)</b> shaped and lies slightly more 
        medially and inferiorly than the right. It is related to the tail of the pancreas and the splenic vessels.</p>
        <ul>
          <li><b>Anteriorly:</b> <b class="purple-term">Stomach</b> (with the lesser sac/omental bursa between them), 
          <b class="purple-term">tail of the pancreas</b> and <b class="purple-term">splenic artery</b></li>
          <li><b>Posteriorly:</b> <b class="purple-term">Diaphragm</b></li>
          <li><b>Inferiorly:</b> Superior pole of the <b>left kidney</b></li>
          <li><b>Medially:</b> <b class="purple-term">Abdominal aorta</b></li>
        </ul>
        <div class="highlight-box">
          <b>Right adrenal:</b> Pyramid-shaped · Posterior to IVC · Bare area of liver anteriorly · Diaphragm posteriorly<br/>
          <b>Left adrenal:</b> Crescent-shaped · Related to stomach, pancreatic tail and splenic artery · Aorta medially
        </div>
      `,
      image: "/images/adrenalglands/adrenalglandrelations.png",
    },
    {
      type: "question",
      text: "The right adrenal gland is closely related to which major vessel anteriorly and medially, making right adrenalectomy more technically challenging?",
      options: [
        "Abdominal aorta",
        "Superior mesenteric artery",
        "Inferior vena cava",
        "Portal vein",
      ],
      correct: 2,
      explanation:
        "The right adrenal gland lies directly posterior to the inferior vena cava (IVC). This close relationship makes right adrenalectomy more technically demanding than the left side, as the surgeon must carefully dissect around the IVC.",
    },
    {
      type: "question",
      text: "What shape is the LEFT adrenal gland?",
      options: [
        "Pyramidal (triangular)",
        "Semilunar (crescent)",
        "Spherical",
        "Ovoid",
      ],
      correct: 1,
      explanation:
        "The left adrenal gland is semilunar (crescent-shaped), while the right adrenal gland is pyramidal (triangular). This difference in shape reflects their slightly different positions and relations.",
    },
    {
      type: "question",
      text: "Which structure lies anteriorly to the left adrenal gland, separated from it by the lesser sac?",
      options: ["Liver", "Duodenum", "Stomach", "Inferior vena cava"],
      correct: 2,
      explanation:
        "The stomach lies anteriorly to the left adrenal gland, with the lesser sac (omental bursa) interposed between them. The liver and IVC are anterior relations of the right adrenal gland.",
    },
    {
      type: "question",
      text: "Which structure is a posterior relation of BOTH adrenal glands?",
      options: ["Inferior vena cava", "Diaphragm", "Pancreas", "Liver"],
      correct: 1,
      explanation:
        "The diaphragm lies posteriorly to both adrenal glands. The IVC is a medial/anterior relation only of the right adrenal gland, the pancreas is an anterior relation of the left, and the liver is anterior to the right.",
    },
    {
      type: "text",
      content: `
        <h3>Lymphatic Drainage</h3>
        <p>The lymphatic drainage of the adrenal glands follows the same path as the venous drainage — it mirrors the arterial supply in reverse.</p>
        <p>Lymph from the adrenal glands drains primarily into the <b class="purple-term">lateral aortic (lumbar) lymph nodes</b>, which run alongside the abdominal aorta and inferior vena cava. From there, lymph drains into the <b class="purple-term">cisterna chyli</b> and then into the <b class="purple-term">thoracic duct</b>.</p>
        <div class="highlight-box">
          Adrenal glands → Lateral aortic (lumbar) lymph nodes → Cisterna chyli → Thoracic duct
        </div>
        <p>This lymphatic pathway is important clinically because malignant tumours of the adrenal gland (such as adrenocortical carcinoma) can spread via lymphatics to the para-aortic lymph nodes, which can be detected on CT or PET imaging as enlarged nodes alongside the aorta.</p>
      `,
      image: "/images/adrenalglands/adrenalglandlymphdrainage.png",
    },
    {
      type: "question",
      text: "Where do the adrenal glands primarily drain their lymph?",
      options: [
        "Inguinal lymph nodes",
        "Lateral aortic (lumbar) lymph nodes",
        "Mediastinal lymph nodes",
        "Renal hilar lymph nodes",
      ],
      correct: 1,
      explanation:
        "Lymph from the adrenal glands drains primarily into the lateral aortic (lumbar) lymph nodes, which lie alongside the abdominal aorta and IVC. From there it continues to the cisterna chyli and thoracic duct.",
    },
    {
      type: "text",
      content: `
        <h3>Innervation of the Adrenal Glands</h3>
        <p>The innervation of the adrenal gland is unusual because the <b>cortex</b> and the <b>medulla</b> are controlled by completely different mechanisms — one hormonal, one neural.</p>

        <h3>Cortex — hormonal control, not direct neural innervation</h3>
        <p>The adrenal cortex has <b>no direct autonomic innervation</b> driving hormone secretion. Instead, it is regulated entirely by <b>hormonal signals</b>:
        <ul>
          <li><b>Zona fasciculata</b> (cortisol): controlled by <b class="purple-term">ACTH</b> (adrenocorticotropic hormone) released from the <b>anterior pituitary</b>, which is itself regulated by <b>CRH</b> (corticotropin-releasing hormone) from the hypothalamus. This is the hypothalamic-pituitary-adrenal (HPA) axis.</li>
          <li><b>Zona glomerulosa</b> (aldosterone): controlled primarily by the <b class="purple-term">renin-angiotensin-aldosterone system (RAAS)</b> and by blood potassium levels — not by ACTH.</li>
          <li><b>Zona reticularis</b> (androgens): also regulated by ACTH.</li>
        </ul>
        </p>
        <p>The cortex does receive some sympathetic nerve fibres that run alongside its blood vessels (vasomotor fibres regulating blood flow), but these do not directly stimulate hormone secretion.</p>

        <h3>Medulla — direct sympathetic innervation</h3>
        <p>The adrenal medulla is a specialised part of the sympathetic nervous system. It is innervated by <b class="purple-term">preganglionic sympathetic fibres</b> (type B — moderately myelinated) that originate in the <b>thoracic spinal cord at levels T5–T11</b>.</p>
        <p>These fibres are unusual because they are <b>preganglionic</b> — they travel all the way to the adrenal medulla without synapsing in any ganglion en route. The chromaffin cells of the medulla act as the <b>postganglionic neurons</b>, releasing catecholamines (adrenaline and noradrenaline) directly into the bloodstream as hormones.</p>
        <p>This arrangement makes the adrenal medulla essentially a <b class="purple-term">"modified sympathetic ganglion"</b> — it evolved from neural crest cells (just like sympathetic ganglia), but instead of extending axons to a target organ, it secretes hormones into the blood, giving the sympathetic response a systemic, whole-body reach.</p>
      `,
      image: "/images/adrenalglands/adrenalglandinnervation2.png",
    },
    {
      type: "text",
      content: `
        <h3>The Pathway of Sympathetic Innervation to the Medulla</h3>
        <p>The preganglionic sympathetic fibres travel from the thoracic spinal cord via the <b class="purple-term">greater splanchnic nerve</b> (T5–T9), and to a lesser extent the <b>lesser splanchnic nerve</b> (T10–T11) and <b>least splanchnic nerve</b> (T12).</p>
        <p>These splanchnic nerves pass through the diaphragm into the abdomen and reach the <b class="purple-term">celiac plexus</b> — a large network of sympathetic nerves surrounding the celiac artery. From the celiac plexus, fibres continue to the adrenal medulla via the <b>suprarenal plexus</b>, synapsing directly onto the chromaffin cells.</p>
        <div class="highlight-box">
          <b>Pathway:</b> Thoracic spinal cord (T5–T11) → Greater/lesser/least splanchnic nerves → Celiac plexus → Suprarenal plexus → Chromaffin cells of the adrenal medulla → Release of adrenaline and noradrenaline into the bloodstream
        </div>
        <p>Because this pathway bypasses the usual postganglionic neuron, <b>acetylcholine</b> is the neurotransmitter released by the preganglionic fibres onto the chromaffin cells. The chromaffin cells then respond by releasing catecholamines into the adrenal sinusoids and into the bloodstream.</p>

        <h3>Why this matters clinically</h3>
        <p>During <b>stress</b> (pain, haemorrhage, hypoglycaemia, fear), the hypothalamus activates the sympathetic nervous system, which rapidly fires these preganglionic fibres. The adrenal medulla then floods the bloodstream with adrenaline and noradrenaline, producing the <b>fight-or-flight response</b>: increased heart rate, vasoconstriction, pupil dilation, bronchodilation, and mobilisation of glucose from glycogen stores in the liver.</p>
        <p>This is also why <b>spinal cord injuries at T5 or above</b> can cause severe cardiovascular instability — the sympathetic outflow to the adrenal medulla and abdominal blood vessels is lost, leading to profound hypotension and an inability to mount a proper stress response.</p>
      `,
      image: "/images/adrenalglands/adrenalglandinnervationpathway.png",
    },
    {
      type: "question",
      text: "The adrenal cortex is NOT directly innervated by autonomic nerves. How is the zona fasciculata primarily regulated?",
      options: [
        "By the sympathetic nervous system via the greater splanchnic nerve",
        "By ACTH released from the anterior pituitary gland",
        "By aldosterone released from the zona glomerulosa",
        "By direct parasympathetic stimulation from the vagus nerve",
      ],
      correct: 1,
      explanation:
        "The zona fasciculata produces cortisol and is regulated by ACTH (adrenocorticotropic hormone) from the anterior pituitary, which is itself stimulated by CRH from the hypothalamus. This is the hypothalamic-pituitary-adrenal (HPA) axis. Unlike the adrenal medulla, the cortex has no direct sympathetic innervation driving hormone secretion.",
    },
    {
      type: "question",
      text: "Why is the adrenal medulla described as a 'modified sympathetic ganglion'?",
      options: [
        "Because it is innervated by parasympathetic fibres from the vagus nerve",
        "Because it contains preganglionic sympathetic fibres that synapse on chromaffin cells, which act as postganglionic neurons releasing hormones into the blood",
        "Because it has the same structure as the celiac plexus",
        "Because it regulates blood pressure through the renin-angiotensin system",
      ],
      correct: 1,
      explanation:
        "The adrenal medulla is called a modified sympathetic ganglion because it is embryologically derived from neural crest cells (like sympathetic ganglia) and is innervated by preganglionic sympathetic fibres. The chromaffin cells act as modified postganglionic neurons — but instead of releasing noradrenaline at a synapse onto a target organ, they release catecholamines directly into the bloodstream as hormones, giving a systemic reach.",
    },
    {
      type: "question",
      text: "At which spinal cord levels do the preganglionic sympathetic fibres that innervate the adrenal medulla originate?",
      options: ["C4–C8", "T1–T4", "T5–T11", "L1–L3"],
      correct: 2,
      explanation:
        "The preganglionic sympathetic fibres innervating the adrenal medulla originate from the thoracic spinal cord at T5–T11. They travel via the greater splanchnic nerve (T5–T9) and lesser/least splanchnic nerves (T10–T12), pass through the diaphragm, and reach the chromaffin cells of the adrenal medulla via the celiac plexus and suprarenal plexus.",
    },
    {
      type: "question",
      text: "What neurotransmitter is released by the preganglionic sympathetic fibres onto the chromaffin cells of the adrenal medulla?",
      options: ["Noradrenaline", "Adrenaline", "Acetylcholine", "Dopamine"],
      correct: 2,
      explanation:
        "Preganglionic sympathetic fibres always release acetylcholine — this applies both to fibres synapsing in ganglia and to those reaching the adrenal medulla. The chromaffin cells then respond to acetylcholine stimulation by secreting adrenaline (epinephrine) and noradrenaline (norepinephrine) into the bloodstream.",
    },
    {
      type: "flashcard",
      front:
        "Compare the innervation of the adrenal cortex vs the adrenal medulla.",
      back: `<table style="width:100%; border-collapse: collapse; font-size: 0.9em;">
        <tr style="background:#f3f4f6">
          <th style="padding:4px 8px; text-align:left"></th>
          <th style="padding:4px 8px">Cortex</th>
          <th style="padding:4px 8px">Medulla</th>
        </tr>
        <tr>
          <td style="padding:4px 8px"><b>Control type</b></td>
          <td style="padding:4px 8px; text-align:center">Hormonal (HPA axis / RAAS)</td>
          <td style="padding:4px 8px; text-align:center">Neural (sympathetic)</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 8px"><b>Regulator</b></td>
          <td style="padding:4px 8px; text-align:center">ACTH / RAAS / K+</td>
          <td style="padding:4px 8px; text-align:center">Preganglionic T5–T11</td>
        </tr>
        <tr>
          <td style="padding:4px 8px"><b>Main nerve</b></td>
          <td style="padding:4px 8px; text-align:center">None direct</td>
          <td style="padding:4px 8px; text-align:center">Greater splanchnic nerve</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 8px"><b>Neurotransmitter</b></td>
          <td style="padding:4px 8px; text-align:center">N/A</td>
          <td style="padding:4px 8px; text-align:center">Acetylcholine (preganglionic)</td>
        </tr>
        <tr>
          <td style="padding:4px 8px"><b>Output</b></td>
          <td style="padding:4px 8px; text-align:center">Cortisol, aldosterone, androgens</td>
          <td style="padding:4px 8px; text-align:center">Adrenaline, noradrenaline</td>
        </tr>
      </table>`,
      hint: "Cortex = hormonal, Medulla = neural",
    },
  ],
};
